import type { components } from '../../api/schema'

// The benchmark's API shapes (spec 2026-09-13-01), named once.
export type KPIDefinition = components['schemas']['KPIDefinition']
export type Distribution = components['schemas']['BenchmarkDistributionResponse']
export type BenchmarkPoint = components['schemas']['BenchmarkPoint']
export type BenchmarkCohort = components['schemas']['BenchmarkCohort']
export type BenchmarkStats = NonNullable<components['schemas']['BenchmarkStats']>
export type AssetBenchmark = components['schemas']['BenchmarkAssetResponse']
export type Strip = components['schemas']['BenchmarkStrip']
export type Decomposition = components['schemas']['BenchmarkDecompositionResponse']
export type Sources = components['schemas']['BenchmarkSourcesResponse']
export type BenchmarkAssetRef = components['schemas']['BenchmarkAssetRef']

/** The metric the page opens on. */
export const DEFAULT_METRIC = 'building_opex_per_unit_12m'

/** Size strata — units stratify the cohort, they are never an axis. */
export const STRATA = ['lt20', '20_49', '50_99', 'gte100'] as const

/** Reads a {en, fr} bag in the active language, English as the fallback. */
export function pick(bag: Record<string, string> | undefined, lang: string): string {
    if (!bag) return ''
    return bag[lang] || bag.en || bag.fr || Object.values(bag)[0] || ''
}

/** A signed percentage for a gap to the median: "+64 %", "−12 %". */
export function formatGap(delta: number | null | undefined, locale: string): string {
    if (delta === null || delta === undefined) return '—'
    const formatted = new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: 0,
        signDisplay: 'exceptZero',
    }).format(delta)
    return formatted
}

/** A KPI value: money per denominator, or a ratio as a percentage. */
export function formatKPIValue(unit: string, value: number | null | undefined, currency: string, locale: string): string {
    if (value === null || value === undefined) return '—'
    if (unit === 'ratio') {
        return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(value)
    }
    if (!currency) {
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)
    }
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: Math.abs(value) < 100 && !Number.isInteger(value) ? 1 : 0,
    }).format(value)
}

/**
 * The money above the median a subject carries, in major units. For a money
 * KPI the value is per denominator unit (per unit, per m², per level); for a
 * ratio the denominator is itself money in minor units.
 */
export function aboveMedian(unit: string, point: { value: number; denominator: number }, median: number): number {
    const gap = point.value - median
    return unit === 'ratio' ? (gap * point.denominator) / 100 : gap * point.denominator
}

/** The last `count` closed months, newest first, as YYYY-MM. */
export function closedMonths(now: Date, count: number): string[] {
    const out: string[] = []
    for (let i = 1; i <= count; i++) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
        out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
    }
    return out
}

/** The building a subject belongs to: itself when it is one. */
export function buildingOf(asset: BenchmarkAssetRef): { uid: string; label: string } {
    if (asset.building) {
        return { uid: asset.building.uid, label: assetLabel(asset.building) }
    }
    return { uid: asset.uid, label: assetLabel(asset) }
}

/** "BG001 Résidence des Lilas", or the name alone. */
export function assetLabel(asset: { code?: string; name?: string }): string {
    const code = asset.code?.trim()
    const name = asset.name?.trim()
    if (code && name) return `${code} ${name}`
    return code || name || '—'
}

/**
 * ECharts paints on a canvas and never resolves CSS custom properties: resolve
 * the token against the document, which carries the active theme.
 */
export function cssToken(name: string, fallback: string): string {
    if (typeof window === 'undefined') return fallback
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return v || fallback
}

/** Quotes a CSV cell. */
export function csvCell(v: string | number | null | undefined): string {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
